import React, { useEffect, useMemo, useState, useRef } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { FileSpreadsheet, Pencil, Plus, RefreshCcw, Search, Upload, AlertTriangle, CheckCircle } from "lucide-react";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest, payrollSaveResourceRequest } from "../../../redux/actions/payrollActions";
import { postRequest, API_BASE_URL } from "../../../../api";
import "./PaieCyclePages.css";

const nameOfUser = (user) => user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || `#${user?.id}`;

const ElementsVariablesPaiePage = () => {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const users = useSelector((state) => state.user?.users || []);
    const [filters, setFilters] = useState({ userId: "", periodeId: "" });
    const [editing, setEditing] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [bulkText, setBulkText] = useState("");
    const [form, setForm] = useState({ userId: "", periodeId: "", rubriqueId: "", typeHeureSupplementaireId: "", valeur: "", commentaire: "" });

    // Import states
    const [parsedItems, setParsedItems] = useState([]);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const fileInputRef = useRef(null);

    useEffect(() => {
        dispatch(getAllUsersRequest());
        ["periodes", "rubriques", "typesHeuresSupplementaires", "elementsVariables"].forEach((resource) => dispatch(payrollFetchResourceRequest(resource)));
    }, [dispatch]);

    const rows = useMemo(() => {
        return (payroll.elementsVariables || []).filter((row) => {
            const userOk = !filters.userId || String(row.userId) === String(filters.userId);
            const periodeOk = !filters.periodeId || String(row.periodeId) === String(filters.periodeId);
            return userOk && periodeOk;
        });
    }, [payroll.elementsVariables, filters]);

    const save = () => {
        if (!form.userId || !form.periodeId || !form.rubriqueId || form.valeur === "") {
            Swal.fire({ icon: "warning", text: "Collaborateur, periode, rubrique et valeur sont obligatoires." });
            return;
        }
        dispatch(payrollSaveResourceRequest("elementsVariables", {
            userId: Number(form.userId),
            periodeId: Number(form.periodeId),
            rubriqueId: Number(form.rubriqueId),
            typeHeureSupplementaireId: form.typeHeureSupplementaireId ? Number(form.typeHeureSupplementaireId) : null,
            valeur: Number(form.valeur),
            commentaire: form.commentaire || null,
        }, editing?.id || null));
        setEditing(null);
        setForm({ userId: "", periodeId: "", rubriqueId: "", typeHeureSupplementaireId: "", valeur: "", commentaire: "" });
    };

    const edit = (row) => {
        setEditing(row);
        setForm({
            userId: row.userId || "",
            periodeId: row.periodeId || "",
            rubriqueId: row.rubriqueId || "",
            typeHeureSupplementaireId: row.typeHeureSupplementaireId || "",
            valeur: row.valeur || "",
            commentaire: row.commentaire || "",
        });
    };

    const handleTextParse = (text) => {
        if (!text.trim()) {
            setParsedItems([]);
            return;
        }
        const lines = text.split("\n");
        const items = [];

        lines.forEach((line, index) => {
            const trimmedLine = line.trim();
            if (!trimmedLine) return;

            // support semicolon or comma
            const parts = trimmedLine.includes(";") ? trimmedLine.split(";") : trimmedLine.split(",");
            const matricule = parts[0]?.trim() || "";
            const periodStr = parts[1]?.trim() || "";
            const rubriqueCode = parts[2]?.trim() || "";
            const typeHsCode = parts[3]?.trim() || "";
            const valStr = parts[4]?.trim() || "";
            const comm = parts[5]?.trim() || "";

            const errors = [];
            const warnings = [];

            // 1. Resolve User
            const foundUser = users.find(
                (u) =>
                    String(u.employeeCode).toLowerCase() === matricule.toLowerCase() ||
                    String(u.email).toLowerCase() === matricule.toLowerCase() ||
                    String(u.username).toLowerCase() === matricule.toLowerCase()
            );
            if (!foundUser) {
                errors.push(`Collaborateur introuvable pour "${matricule}"`);
            }

            // 2. Resolve Period
            const foundPeriode = (payroll.periodes || []).find((p) => {
                const checkStr = `${String(p.mois).padStart(2, "0")}/${p.annee}`;
                return checkStr === periodStr;
            });
            if (!foundPeriode) {
                errors.push(`Période introuvable pour "${periodStr}"`);
            }

            // 3. Resolve Rubrique
            const foundRubrique = (payroll.rubriques || []).find(
                (r) => String(r.code).toLowerCase() === rubriqueCode.toLowerCase()
            );
            if (!foundRubrique) {
                errors.push(`Rubrique introuvable pour "${rubriqueCode}"`);
            }

            // 4. Resolve Type HS
            let foundTypeHs = null;
            if (typeHsCode) {
                foundTypeHs = (payroll.typesHeuresSupplementaires || []).find(
                    (h) => String(h.code).toLowerCase() === typeHsCode.toLowerCase()
                );
                if (!foundTypeHs) {
                    warnings.push(`Type HS introuvable pour "${typeHsCode}" (sera ignoré)`);
                }
            }

            // 5. Parse value
            const parsedVal = parseFloat(valStr);
            if (isNaN(parsedVal) || parsedVal < 0) {
                errors.push(`Valeur invalide: "${valStr}"`);
            }

            items.push({
                lineNum: index + 1,
                raw: trimmedLine,
                matricule,
                periodStr,
                rubriqueCode,
                typeHsCode,
                valStr,
                comm,
                userId: foundUser?.id || null,
                userFullName: foundUser ? nameOfUser(foundUser) : null,
                periodeId: foundPeriode?.id || null,
                rubriqueId: foundRubrique?.id || null,
                typeHeureSupplementaireId: foundTypeHs?.id || null,
                valeur: parsedVal,
                errors,
                warnings,
            });
        });

        setParsedItems(items);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target.result;
            setBulkText(text);
            handleTextParse(text);
        };
        reader.readAsText(file);
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const startImport = async () => {
        const valids = parsedItems.filter((item) => item.errors.length === 0);
        if (valids.length === 0) {
            Swal.fire({ icon: "error", title: "Erreur", text: "Aucune ligne valide à importer." });
            return;
        }

        const confirm = await Swal.fire({
            icon: "question",
            title: "Lancer l'import ?",
            text: `Voulez-vous importer les ${valids.length} ligne(s) valide(s) ?`,
            showCancelButton: true,
            confirmButtonText: "Importer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#fcd64c"
        });

        if (!confirm.isConfirmed) return;

        setImporting(true);
        setImportProgress(0);
        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < valids.length; i++) {
            const item = valids[i];
            try {
                await postRequest({
                    url: `${API_BASE_URL}/api/elements-variables-paie`,
                    data: {
                        userId: item.userId,
                        periodeId: item.periodeId,
                        rubriqueId: item.rubriqueId,
                        typeHeureSupplementaireId: item.typeHeureSupplementaireId,
                        valeur: item.valeur,
                        commentaire: item.comm || null,
                    },
                    token: sessionStorage.getItem("token") || localStorage.getItem("token"),
                });
                successCount++;
            } catch (err) {
                failCount++;
            }
            setImportProgress(Math.round(((i + 1) / valids.length) * 100));
        }

        setImporting(false);
        Swal.fire({
            icon: failCount > 0 ? "warning" : "success",
            title: "Importation terminée",
            text: `${successCount} ligne(s) importée(s) avec succès. ${failCount} échec(s).`,
        });

        dispatch(payrollFetchResourceRequest("elementsVariables"));
        setImportOpen(false);
        setBulkText("");
        setParsedItems([]);
    };

    return (
        <div className="pcyc-page">
            <div className="pcyc-header"><div><h2><FileSpreadsheet size={18} />Saisie des elements variables</h2><p>Saisie par collaborateur et periode: jours travailles, heures sup, primes ponctuelles.</p></div><button className="pcyc-btn pcyc-btn-blue" type="button" onClick={() => dispatch(payrollFetchResourceRequest("elementsVariables"))}><RefreshCcw size={15} />Actualiser</button></div>
            <div className="pcyc-toolbar">
                <label><span>Collaborateur</span><select value={filters.userId} onChange={(e) => setFilters({ ...filters, userId: e.target.value })}><option value="">Tous</option>{users.map((u) => <option key={u.id} value={u.id}>{nameOfUser(u)}</option>)}</select></label>
                <label><span>Periode</span><select value={filters.periodeId} onChange={(e) => setFilters({ ...filters, periodeId: e.target.value })}><option value="">Toutes</option>{(payroll.periodes || []).map((p) => <option key={p.id} value={p.id}>{String(p.mois).padStart(2, "0")}/{p.annee}</option>)}</select></label>
                <button className="pcyc-btn pcyc-btn-primary" type="button" onClick={() => setImportOpen(true)}><Upload size={15} />Alimentation par lot</button>
                <div className="pcyc-meta">{rows.length} ligne(s)</div>
            </div>

            <div className="pcyc-grid pcyc-grid-form">
                <section className="pcyc-card">
                    <div className="pcyc-card-title"><Plus size={16} />{editing ? "Modifier une ligne" : "Saisir une ligne variable"}</div>
                    <div className="pcyc-form-grid">
                        <label><span>Collaborateur</span><select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}><option value="">Selectionner</option>{users.map((u) => <option key={u.id} value={u.id}>{nameOfUser(u)}</option>)}</select></label>
                        <label><span>Periode</span><select value={form.periodeId} onChange={(e) => setForm({ ...form, periodeId: e.target.value })}><option value="">Selectionner</option>{(payroll.periodes || []).map((p) => <option key={p.id} value={p.id}>{String(p.mois).padStart(2, "0")}/{p.annee}</option>)}</select></label>
                        <label><span>Rubrique</span><select value={form.rubriqueId} onChange={(e) => setForm({ ...form, rubriqueId: e.target.value })}><option value="">Selectionner</option>{(payroll.rubriques || []).filter((r) => r.active !== false).map((r) => <option key={r.id} value={r.id}>{r.code} - {r.libelle}</option>)}</select></label>
                        <label><span>Type heure sup</span><select value={form.typeHeureSupplementaireId} onChange={(e) => setForm({ ...form, typeHeureSupplementaireId: e.target.value })}><option value="">Aucun</option>{(payroll.typesHeuresSupplementaires || []).map((h) => <option key={h.id} value={h.id}>{h.code} - {h.libelle}</option>)}</select></label>
                        <label><span>Valeur</span><input type="number" step="0.0001" value={form.valeur} onChange={(e) => setForm({ ...form, valeur: e.target.value })} /></label>
                        <label><span>Commentaire</span><input value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} /></label>
                    </div>
                    <div className="pcyc-actions">
                        {editing && <button className="pcyc-btn pcyc-btn-light" type="button" onClick={() => { setEditing(null); setForm({ userId: "", periodeId: "", rubriqueId: "", typeHeureSupplementaireId: "", valeur: "", commentaire: "" }); }}>Annuler</button>}
                        <button className="pcyc-btn pcyc-btn-primary" type="button" onClick={save}>Enregistrer</button>
                    </div>
                </section>
                <section className="pcyc-card"><div className="pcyc-card-title"><Search size={16} />Utilisation</div><p className="pcyc-help">Pour les heures supplementaires, choisissez une rubrique de gain et un type HS. La valeur represente le nombre d'heures. Pour une prime, laissez le type HS vide et saisissez le montant.</p></section>
            </div>

            <div className="pcyc-table-card"><table className="pcyc-table"><thead><tr><th>Collaborateur</th><th>Periode</th><th>Rubrique</th><th>Type HS</th><th>Valeur</th><th>Commentaire</th><th>Actions</th></tr></thead><tbody>{rows.length === 0 ? <tr><td colSpan="7" className="pcyc-empty">Aucune variable</td></tr> : rows.map((row) => <tr key={row.id}><td>{row.userFullName || row.userId}</td><td>{row.mois ? `${String(row.mois).padStart(2, "0")}/${row.annee}` : row.periodeId}</td><td>{row.rubriqueLibelle || row.rubriqueId}</td><td>{row.typeHeureSupplementaireLibelle || "-"}</td><td>{row.valeur}</td><td>{row.commentaire || "-"}</td><td className="pcyc-row-actions"><button type="button" onClick={() => edit(row)}><Pencil size={14} /></button></td></tr>)}</tbody></table></div>

            {importOpen && (
                <div className="pcyc-overlay" onClick={() => !importing && setImportOpen(false)}>
                    <div className="pcyc-modal" style={{ width: "800px" }} onClick={(e) => e.stopPropagation()}>
                        <div className="pcyc-modal-header">
                            <h3>Alimentation des variables par lot</h3>
                            <button type="button" disabled={importing} onClick={() => setImportOpen(false)}>x</button>
                        </div>
                        <div className="pcyc-modal-body">
                            {importing ? (
                                <div style={{ textAlign: "center", padding: "30px 10px" }}>
                                    <h4 style={{ color: "#2f4595", marginBottom: "12px" }}>Importation en cours...</h4>
                                    <div className="pcyc-progress" style={{ margin: "20px 0" }}>
                                        <span style={{ width: `${importProgress}%` }} />
                                    </div>
                                    <strong className="pcyc-progress-value">{importProgress}%</strong>
                                </div>
                            ) : (
                                <>
                                    <p className="pcyc-help">
                                        Format attendu : <code>matricule;periode;rubrique;type_hs;valeur;commentaire</code> (ex: <code>EMP001;06/2026;PRIMES;;1500;Prime exceptionnelle</code>).
                                    </p>
                                    <textarea
                                        rows={6}
                                        value={bulkText}
                                        onChange={(e) => {
                                            setBulkText(e.target.value);
                                            handleTextParse(e.target.value);
                                        }}
                                        placeholder="EMP001;06/2026;HS;HS_25;8;Heures sup semaine 1"
                                    />
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: "none" }}
                                        accept=".csv,.txt"
                                        onChange={handleFileChange}
                                    />

                                    {parsedItems.length > 0 && (
                                        <div style={{ marginTop: "14px", maxHeight: "250px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "8px" }}>
                                            <table className="pcyc-table" style={{ minWidth: "100%", margin: 0 }}>
                                                <thead>
                                                    <tr>
                                                        <th>Ligne</th>
                                                        <th>Collaborateur</th>
                                                        <th>Période</th>
                                                        <th>Rubrique</th>
                                                        <th>Valeur</th>
                                                        <th>Statut</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {parsedItems.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>{item.lineNum}</td>
                                                            <td>
                                                                {item.userId ? (
                                                                    <span style={{ color: "#2e7d32" }}>{item.userFullName}</span>
                                                                ) : (
                                                                    <span style={{ color: "#d32f2f" }}>{item.matricule} (Inconnu)</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span style={{ color: item.periodeId ? "#2e7d32" : "#d32f2f" }}>{item.periodStr}</span>
                                                            </td>
                                                            <td>
                                                                <span style={{ color: item.rubriqueId ? "#2e7d32" : "#d32f2f" }}>{item.rubriqueCode}</span>
                                                            </td>
                                                            <td>{item.valeur || 0}</td>
                                                            <td>
                                                                {item.errors.length > 0 ? (
                                                                    <span title={item.errors.join(", ")} style={{ color: "#d32f2f", display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: 700 }}>
                                                                        <AlertTriangle size={13} /> {item.errors.length} erreur(s)
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ color: "#2e7d32", display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: 700 }}>
                                                                        <CheckCircle size={13} /> Valide
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <div className="pcyc-actions">
                                        <button className="pcyc-btn pcyc-btn-light" type="button" onClick={triggerFileInput}>
                                            Choisir fichier CSV
                                        </button>
                                        <button
                                            className="pcyc-btn pcyc-btn-primary"
                                            type="button"
                                            disabled={parsedItems.filter((i) => i.errors.length === 0).length === 0}
                                            onClick={startImport}
                                        >
                                            Importer ({parsedItems.filter((i) => i.errors.length === 0).length}) ligne(s) valide(s)
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ElementsVariablesPaiePage;
